import IndicatorButton from 'components/indicator/IndicatorButton';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';
import { FormProvider } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import WsModalContentConfirm from 'pages/ws/components/modalContentConfirm/WsModalContentConfirm';
import WsModalContentPublish from 'pages/ws/components/modalContentPublish/WsModalContentPublish';
import WsContentEditBase from 'pages/ws/components/contentEditBase/WsContentEditBase';
import WsContentEditDesign from 'pages/ws/components/contentEditDesign/WsContentEditDesign';
import WsContentEditPopupEmbed from 'pages/ws/components/contentEditPopupEmbed/WsContentEditPopupEmbed';
import WsContentEditReport from 'pages/ws/components/contentEditReport/WsContentEditReport';
import WsContentEditRespondentFields from 'pages/ws/components/contentEditRespondentFields/WsContentEditRespondentFields';
import WsDiagnosisEditChatbot from 'pages/ws/diagnosisEdit/components/diagnosisEditChatbot/WsDiagnosisEditChatbot';
import WsDiagnosisEditQuestions from 'pages/ws/diagnosisEdit/components/diagnosisEditQuestions/WsDiagnosisEditQuestions';
import WsDiagnosisEditResultOptions from 'pages/ws/diagnosisEdit/components/diagnosisEditResultOptions/WsDiagnosisEditResultOptions';
import WsDiagnosisEditResults from 'pages/ws/diagnosisEdit/components/diagnosisEditResults/WsDiagnosisEditResults';
import WsDiagnosisEditStatusInfo from 'pages/ws/diagnosisEdit/components/diagnosisEditStatusInfo/WsDiagnosisEditStatusInfo';
import { useWsDiagnosisEdit } from 'pages/ws/diagnosisEdit/hooks/useWsDiagnosisEdit';

import type { JSX } from 'react';

/**
 * 診断型コンテンツ作成・編集画面
 * ロジックは useWsDiagnosisEdit に委譲し、JSX のみを担当する。
 */
export default function WsDiagnosisEdit(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const {
    useFormMethods,
    questionFieldArray,
    resultFieldArray,
    respondentFieldArray,
    initialized,
    processingOnSave,
    processingOnPreview,
    processingOnPublish,
    processingOnPublishTerminate,
    processingOnReserveCancel,
    processingOnDelete,
    processingOnCreateQuestion,
    diagnosisState,
    isExpiredPlan,
    canPublished,
    isUnpublished,
    isReadOnly,
    modalPublishOpenState,
    setModalPublishOpenState,
    modalConfirmOpenState,
    setModalConfirmOpenState,
    modalConfirmTitle,
    onSave,
    onPreview,
    onPublishModalOpen,
    onConfirmModalSubmit,
    onPublishModalSubmit,
    onPublishTerminate,
    onReserveCancel,
    onDelete,
    onCreateDiagnosisRef,
    navigate,
    confirm,
  } = useWsDiagnosisEdit();

  const { setValue, getValues } = useFormMethods;

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <FormProvider {...useFormMethods}>
        {/* 初期化中の表示 */}
        <IndicatorScreen visible={!initialized || processingOnPublish} />
        {/* 初期化後の表示 */}
        {initialized && (
          <>
            <section className="section-header">
              <div className="container">
                <a onClick={() => navigate(-1)} className="btn-back-list mt-0 mb-3">
                  戻る
                </a>
                <div className="d-md-flex d-flex-header">
                  <div className="flex-shrink-0 flex-left">
                    <h2 className="flex-title">診断型コンテンツ作成・編集</h2>
                  </div>
                  <div className="flex-grow-1 flex-right">
                    <ul className="list-inline list-btn mb-0">
                      {/* 保存ボタン */}
                      {isUnpublished() && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onSave}
                            disabled={processingOnSave}
                            className="btn btn-common green w-100"
                          >
                            <IndicatorButton visible={processingOnSave} />
                            保存する
                          </button>
                        </li>
                      )}

                      {/* 公開停止ボタン */}
                      {diagnosisState?.diagnosisStatusDiv ==
                        DivConst.items.diagnosisStatus.published.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onPublishTerminate}
                            disabled={processingOnPublishTerminate}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnPublishTerminate} />
                            公開を停止する
                          </button>
                        </li>
                      )}
                      {/* 予約停止ボタン */}
                      {diagnosisState?.diagnosisStatusDiv ==
                        DivConst.items.diagnosisStatus.reserved.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onReserveCancel}
                            disabled={processingOnReserveCancel}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnReserveCancel} />
                            予約を取り消す
                          </button>
                        </li>
                      )}
                      {/* プレビューボタン */}
                      <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                        <button
                          onClick={onPreview}
                          disabled={processingOnPreview}
                          className="btn btn-common outline-blue w-100"
                        >
                          <IndicatorButton visible={processingOnPreview} />
                          {`${isUnpublished() ? '保存して' : ''}プレビュー`}
                        </button>
                      </li>
                      {/* 公開ボタン */}
                      {isUnpublished() && (
                        <li className="list-inline-item list-item-lg ms-lg-4 mb-2">
                          <div
                            data-tooltip-id="custom-tooltip"
                            data-tooltip-content={
                              isExpiredPlan
                                ? 'プランの期限がきれました。プランのアップグレードをご検討ください。'
                                : 'トライアルプランの公開は1つまでです。他の診断を一度非公開にするかプランのアップグレードをご検討ください。'
                            }
                            className="tooltip-wrapper"
                          >
                            <button
                              onClick={onPublishModalOpen}
                              disabled={isExpiredPlan || !canPublished}
                              className="btn btn-common moderate-blue w-100"
                            >
                              公開する
                            </button>
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                {/* 削除ボタン */}
                {isUnpublished() && diagnosisState && (
                  <div className="d-md-flex d-flex-header mt-2">
                    <div className="flex-shrink-0 flex-left"></div>
                    <div className="flex-grow-1 flex-right">
                      <ul className="list-inline list-btn mb-0">
                        <li className="list-inline-item list-item-lg ms-lg-4 pe-1">
                          <a
                            onClick={() =>
                              confirm({
                                title: '診断型コンテンツの削除',
                                message:
                                  '診断型コンテンツや獲得リストなどの関連データを全て削除します。この操作は取り消せません。',
                                onSubmit: onDelete,
                              })
                            }
                            className="text-danger"
                          >
                            <IndicatorButton visible={processingOnDelete} />
                            削除する
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* ステータスや公開情報の表示 */}
                <WsDiagnosisEditStatusInfo diagnosis={diagnosisState} />
              </div>
            </section>

            <section className="section-header"></section>

            {/* 基本設定 */}
            <div data-section="basic">
              <WsContentEditBase
                readOnly={isReadOnly()}
                contentCategory={DivConst.items.contentsType.diagnosis}
                onCreate={() => onCreateDiagnosisRef.current?.()}
                processing={processingOnCreateQuestion}
              />
            </div>
            {/* 取得項目設定 */}
            <WsContentEditRespondentFields
              readOnly={isReadOnly()}
              contentCategory={DivConst.items.contentsType.diagnosis}
              respondentFieldArray={respondentFieldArray}
            />
            {/* デザイン設定 */}
            <WsContentEditDesign
              readOnly={isReadOnly()}
              contentCategory={DivConst.items.contentsType.diagnosis}
            />
            {/* 判定結果選択肢 */}
            <div data-section="results">
              <WsDiagnosisEditResultOptions readOnly={isReadOnly()} fieldArray={resultFieldArray} />
            </div>
            {/* 設問設定 */}
            <div data-section="questions">
              <WsDiagnosisEditQuestions readOnly={isReadOnly()} fieldArray={questionFieldArray} />
            </div>
            {/* 判定結果 */}
            <div data-section="results">
              <WsDiagnosisEditResults readOnly={isReadOnly()} fieldArray={resultFieldArray} />
            </div>
            {/* レポートメール設定 */}
            <WsContentEditReport
              readOnly={isReadOnly()}
              contentCategory={DivConst.items.contentsType.diagnosis}
            />
            {/* ポップアップ埋め込みタグ */}
            <WsContentEditPopupEmbed readOnly={isReadOnly()} diagnosisState={diagnosisState} />

            <section className="section-header">
              <div className="container">
                <div className="d-md-flex d-flex-header">
                  <div className="flex-shrink-0 flex-left"></div>
                  <div className="flex-grow-1 flex-right">
                    <ul className="list-inline list-btn mb-0">
                      {/* 保存ボタン */}
                      {isUnpublished() && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onSave}
                            disabled={processingOnSave}
                            className="btn btn-common green w-100"
                          >
                            <IndicatorButton visible={processingOnSave} />
                            保存する
                          </button>
                        </li>
                      )}

                      {/* 公開停止ボタン */}
                      {diagnosisState?.diagnosisStatusDiv ==
                        DivConst.items.diagnosisStatus.published.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onPublishTerminate}
                            disabled={processingOnPublishTerminate}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnPublishTerminate} />
                            公開を停止する
                          </button>
                        </li>
                      )}
                      {/* 予約停止ボタン */}
                      {diagnosisState?.diagnosisStatusDiv ==
                        DivConst.items.diagnosisStatus.reserved.value && (
                        <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                          <button
                            onClick={onReserveCancel}
                            disabled={processingOnReserveCancel}
                            className="btn btn-common btn-danger w-100"
                          >
                            <IndicatorButton visible={processingOnReserveCancel} />
                            予約を取り消す
                          </button>
                        </li>
                      )}
                      {/* プレビューボタン */}
                      <li className="list-inline-item list-item-md ms-lg-4 mb-2">
                        <button
                          onClick={onPreview}
                          disabled={processingOnPreview}
                          className="btn btn-common outline-blue w-100"
                        >
                          <IndicatorButton visible={processingOnPreview} />
                          {`${isUnpublished() ? '保存して' : ''}プレビュー`}
                        </button>
                      </li>
                      {/* 公開ボタン */}
                      {isUnpublished() && (
                        <li className="list-inline-item list-item-lg ms-lg-4 mb-2">
                          <div
                            data-tooltip-id="custom-tooltip"
                            data-tooltip-content={
                              isExpiredPlan
                                ? 'プランの期限がきれました。プランのアップグレードをご検討ください。'
                                : 'トライアルプランの公開は1つまでです。他の診断を一度非公開にするかプランのアップグレードをご検討ください。'
                            }
                            className="tooltip-wrapper"
                          >
                            <button
                              onClick={onPublishModalOpen}
                              disabled={isExpiredPlan || !canPublished}
                              className="btn btn-common moderate-blue w-100"
                            >
                              公開する
                            </button>
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </FormProvider>

      {/* 診断型コンテンツ公開設定モーダル */}
      <WsModalContentPublish
        open={modalPublishOpenState}
        setOpen={setModalPublishOpenState}
        publishStartAt={diagnosisState?.publishStartAt}
        publishEndAt={diagnosisState?.publishEndAt}
        noIndexFlag={diagnosisState?.noIndexFlag}
        onSubmit={attr => onPublishModalSubmit(attr)}
      />
      {
        // トライアルプラン（利用期間切れ）もしくは公開上限の場合以外は表示しない
        (isExpiredPlan || !canPublished) && (
          <Tooltip id="custom-tooltip" place="top" variant="info" />
        )
      }

      {/* 未設定確認モーダル */}
      <WsModalContentConfirm
        open={modalConfirmOpenState}
        setOpen={setModalConfirmOpenState}
        title={modalConfirmTitle}
        onSubmit={onConfirmModalSubmit}
      />
      {/* チャットボットコンポーネント */}
      <WsDiagnosisEditChatbot
        getValues={getValues}
        setValue={setValue}
        questionFieldArray={questionFieldArray}
        resultFieldArray={resultFieldArray}
        onCreateRef={onCreateDiagnosisRef}
      />
    </>
  );
}
